import { Component } from '@theme/component';

const WISHLIST_KEY = 'mox_wishlist';
const WISHLIST_EVENT = 'wishlist:updated';
const WISHLIST_CARD_SECTION_ID = 'section-rendering-wishlist-product-card';
const WISHLIST_RENDER_CONCURRENCY = 4;
const WISHLIST_EAGER_CARD_COUNT = 4;
const WISHLIST_HIGH_PRIORITY_CARD_COUNT = 2;

/**
 * @returns {Array<{ id: string, handle: string, title: string, price: string, image: string, url: string }>}
 */
function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * @param {Array<{ id: string, handle: string, title: string, price: string, image: string, url: string }>} list
 */
function saveWishlist(list) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { count: list.length } }));
}

/**
 * @param {{ handle?: string, url?: string }} item
 */
function getProductHandle(item) {
  if (item?.handle) return item.handle;
  if (item?.url) return item.url.split('/products/')[1]?.split('?')[0] || '';
  return '';
}

/**
 * @param {string} html
 */
function htmlToElement(html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.firstElementChild;
}

/**
 * @param {string} productId
 */
function isInWishlist(productId) {
  return getWishlist().some((item) => String(item.id) === String(productId));
}

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends Component<Refs>
 */
export class WishlistButton extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#stopPropagation, true);
    document.addEventListener(WishlistChangeEvent.eventName, this.#handleExternalChange);
    window.addEventListener(WISHLIST_EVENT, this.#syncFromStorage);
    this.#syncFromStorage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#stopPropagation, true);
    document.removeEventListener(WishlistChangeEvent.eventName, this.#handleExternalChange);
    window.removeEventListener(WISHLIST_EVENT, this.#syncFromStorage);
  }

  #stopPropagation = (event) => {
    event.stopPropagation();
  };

  #syncFromStorage = () => {
    this.#setActive(isInWishlist(this.productId));
  };

  toggleWishlist = (event) => {
    event?.preventDefault();

    if (isInWishlist(this.productId)) {
      this.#removeFromWishlist();
    } else {
      this.#addToWishlist();
    }
  };

  #addToWishlist() {
    const list = getWishlist();
    list.push({
      id: this.productId,
      handle: this.dataset.handle ?? '',
      title: this.dataset.title ?? '',
      price: this.dataset.price ?? '',
      image: this.dataset.image ?? '',
      url: this.dataset.url ?? '',
    });
    saveWishlist(list);
    this.#setActive(true);
    document.dispatchEvent(new WishlistChangeEvent(this.productId, true));
  }

  #removeFromWishlist() {
    const list = getWishlist().filter((item) => String(item.id) !== String(this.productId));
    saveWishlist(list);
    this.#setActive(false);
    document.dispatchEvent(new WishlistChangeEvent(this.productId, false));
  }

  /**
   * @param {WishlistChangeEvent} event
   */
  #handleExternalChange = (event) => {
    if (event.productId !== this.productId) return;
    this.#setActive(event.active);
  };

  /**
   * @param {boolean} active
   */
  #setActive(active) {
    this.refs.button.setAttribute('aria-pressed', String(active));
    this.classList.toggle('wishlist-button--active', active);
    this.refs.button.setAttribute('aria-label', active ? (this.dataset.labelRemove ?? '') : (this.dataset.labelAdd ?? ''));
  }

  get productId() {
    return this.dataset.productId ?? '';
  }
}

export class WishlistChangeEvent extends CustomEvent {
  /**
   * @param {string} productId
   * @param {boolean} active
   */
  constructor(productId, active) {
    super(WishlistChangeEvent.eventName, { bubbles: true });
    this.productId = productId;
    this.active = active;
  }

  static eventName = 'wishlist:change';
}

class WishlistPage extends Component {
  requiredRefs = ['grid'];

  connectedCallback() {
    super.connectedCallback();
    /** @type {Map<string, Promise<string | null>>} */
    this._cardCache = new Map();
    this._renderToken = 0;
    this._onUpdate = () => this.render();
    window.addEventListener(WISHLIST_EVENT, this._onUpdate);
    requestAnimationFrame(() => this.render());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(WISHLIST_EVENT, this._onUpdate);
  }

  async render() {
    const renderToken = ++this._renderToken;
    const list = getWishlist();
    const emptyEl = this.refs.empty;
    const gridEl = this.refs.grid;
    if (!gridEl) return;

    if (list.length === 0) {
      this.removeAttribute('data-loading');
      this.removeAttribute('data-ready');
      if (emptyEl) emptyEl.hidden = false;
      gridEl.replaceChildren();
      requestAnimationFrame(() => this.setAttribute('data-ready', ''));
      return;
    }

    this.setAttribute('data-loading', '');
    this.removeAttribute('data-ready');
    if (emptyEl) emptyEl.hidden = true;
    const placeholders = list.map(() => this._buildPlaceholder());
    gridEl.replaceChildren(...placeholders);
    requestAnimationFrame(() => this.setAttribute('data-ready', ''));

    const { renderedCount, invalidIds } = await this._renderCardsProgressively(list, placeholders, renderToken);
    if (renderToken !== this._renderToken) return;

    if (invalidIds.size > 0) {
      saveWishlist(getWishlist().filter((item) => !invalidIds.has(String(item.id))));
    }

    this.removeAttribute('data-loading');

    if (renderedCount === 0) {
      if (emptyEl) emptyEl.hidden = false;
      gridEl.replaceChildren();
      requestAnimationFrame(() => this.setAttribute('data-ready', ''));
    }
  }

  /**
   * @param {{ handle?: string, url?: string }} item
   */
  async _fetchCardHtml(item) {
    const handle = getProductHandle(item);
    if (!handle) return { html: null, invalid: true };

    const cachedCard = this._cardCache.get(handle);
    if (cachedCard) return cachedCard;

    const request = fetch(`/products/${encodeURIComponent(handle)}?section_id=${WISHLIST_CARD_SECTION_ID}`)
      .then(async (response) => {
        if (!response.ok) return { html: null, invalid: false };
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const cardHtml = doc.querySelector('[data-wishlist-card]')?.outerHTML || null;
        return { html: cardHtml, invalid: !cardHtml };
      })
      .catch(() => ({ html: null, invalid: false }));

    this._cardCache.set(handle, request);
    return request;
  }

  _buildPlaceholder() {
    const placeholder = document.createElement('div');
    placeholder.className = 'wishlist-page__slot wishlist-page__slot--placeholder';
    placeholder.innerHTML = `
      <div class="wishlist-page__ph-media"></div>
      <div class="wishlist-page__ph-line wishlist-page__ph-line--short"></div>
      <div class="wishlist-page__ph-line"></div>
      <div class="wishlist-page__ph-line wishlist-page__ph-line--price"></div>
    `;
    return placeholder;
  }

  /**
   * @param {Array<{ handle?: string, url?: string }>} list
   * @param {HTMLElement[]} placeholders
   * @param {number} renderToken
   */
  async _renderCardsProgressively(list, placeholders, renderToken) {
    let nextIndex = 0;
    let renderedCount = 0;
    const invalidIds = new Set();
    const workerCount = Math.min(WISHLIST_RENDER_CONCURRENCY, list.length);

    const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < list.length) {
        const currentIndex = nextIndex++;
        const placeholder = placeholders[currentIndex];
        const { html: cardHtml, invalid } = await this._fetchCardHtml(list[currentIndex]);
        if (renderToken !== this._renderToken) return;

        if (invalid) invalidIds.add(String(list[currentIndex].id));
        if (!placeholder?.isConnected) continue;
        if (!cardHtml) {
          placeholder.remove();
          continue;
        }

        const card = htmlToElement(cardHtml);
        if (!(card instanceof HTMLElement)) {
          placeholder.remove();
          continue;
        }

        this._prepareCard(card, currentIndex);
        placeholder.replaceWith(card);
        requestAnimationFrame(() => card.setAttribute('data-ready', ''));
        renderedCount += 1;
      }
    });

    await Promise.all(workers);
    return { renderedCount, invalidIds };
  }

  /**
   * @param {HTMLElement} card
   * @param {number} index
   */
  _prepareCard(card, index) {
    card.classList.add('wishlist-page__slot');
    this._prepareCardImages(card, index);
  }

  /**
   * @param {HTMLElement} card
   * @param {number} index
   */
  _prepareCardImages(card, index) {
    const media = card.querySelector('.wishlist-product-card__media');
    const images = Array.from(card.querySelectorAll('.wishlist-product-card__img'));
    if (images.length === 0) {
      media?.setAttribute('data-media-ready', '');
      return;
    }

    for (const [imageIndex, image] of images.entries()) {
      image.decoding = 'async';

      if (imageIndex === 0 && index < WISHLIST_EAGER_CARD_COUNT) {
        image.loading = 'eager';
        image.setAttribute('fetchpriority', index < WISHLIST_HIGH_PRIORITY_CARD_COUNT ? 'high' : 'auto');
      } else {
        image.loading = 'lazy';
        image.setAttribute('fetchpriority', 'low');
      }

      const markLoaded = () => {
        image.classList.add('is-loaded');
        if (imageIndex === 0) media?.setAttribute('data-media-ready', '');
      };

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
        continue;
      }

      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener(
        'error',
        () => {
          if (imageIndex === 0) media?.setAttribute('data-media-ready', '');
        },
        { once: true }
      );
    }
  }
}

/**
 * Header wishlist link: syncs its count bubble with the localStorage wishlist.
 * The anchor itself is static; only the counter (and its visibility) updates.
 */
export class WishlistHeaderButton extends HTMLElement {
  connectedCallback() {
    this._onUpdate = () => this._update();
    window.addEventListener(WISHLIST_EVENT, this._onUpdate);
    this._update();
  }

  disconnectedCallback() {
    window.removeEventListener(WISHLIST_EVENT, this._onUpdate);
  }

  _update() {
    const count = getWishlist().length;
    const counter = this.querySelector('[data-wishlist-count]');
    if (counter) counter.textContent = String(count);
    this.toggleAttribute('data-has-items', count > 0);
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
if (!customElements.get('wishlist-header-button')) customElements.define('wishlist-header-button', WishlistHeaderButton);
